import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
  type ChartView,
  type ChartViewLabels,
  formatPeriodLabel,
  getComplianceColor,
  useChartPagination,
} from '@src/packages/dashboard/components/ReportSummary';

import type {
  IncidentsBucket,
  OutcomesEvolutionBucket,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

import type { TasksChartFilters } from '../../tasksFilterConfig';
import type { TasksChartViews } from '../types';
import { INCIDENT_CATEGORIES } from '../constants';
import { getEvolutionPoints, getIncidentPoints } from '../historySelectors';

const NO_BUCKETS: OutcomesEvolutionBucket[] = [];
const NO_INCIDENTS: IncidentsBucket[] = [];

/** Compliance evolution and execution incidents charts: series, paging and view mode. */
export const useTasksTrendCharts = (
  chartFilters: TasksChartFilters,
  chartViews: TasksChartViews,
  onChartViewChange: (chart: keyof TasksChartViews, view: ChartView) => void,
  evolutionBuckets: OutcomesEvolutionBucket[] = NO_BUCKETS,
  incidentBuckets: IncidentsBucket[] = NO_INCIDENTS,
) => {
  const intl = useIntl();
  const groupBy = chartFilters?.groupBy ?? 'day';

  const periods = useMemo(
    () => getIncidentPoints(incidentBuckets).map(point => ({
      ...point,
      periodLabel: formatPeriodLabel(point.date, groupBy, intl),
    })),
    [groupBy, incidentBuckets, intl],
  );
  const evolutionPeriods = useMemo(
    () => getEvolutionPoints(evolutionBuckets).map(point => ({
      ...point,
      periodLabel: formatPeriodLabel(point.date, groupBy, intl),
    })),
    [evolutionBuckets, groupBy, intl],
  );
  const evolutionPages = useChartPagination(evolutionPeriods.length, groupBy, chartFilters);
  const incidentPages = useChartPagination(periods.length, groupBy, chartFilters);

  const viewLabels: ChartViewLabels = {
    ariaLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView' }),
    bar: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView.bar' }),
    line: intl.formatMessage({ id: 'dashboards.tasks.summary.chartView.line' }),
  };
  const pagerProps = (pages: typeof evolutionPages, ariaLabelId: string) => ({
    page: pages.page,
    pageSize: pages.pageSize,
    pageCount: pages.pageCount,
    total: pages.total,
    ariaLabel: intl.formatMessage({ id: ariaLabelId }),
    onChange: pages.setPage,
  });

  const incidentCategories = INCIDENT_CATEGORIES.map(category => ({
    ...category,
    label: intl.formatMessage({ id: `dashboards.tasks.summary.incidents.${category.key}` }),
  }));

  const evolution = {
    controls: {
      view: {
        value: chartViews.evolution,
        labels: viewLabels,
        onChange: view => onChartViewChange('evolution', view),
      },
      pager: pagerProps(evolutionPages, 'dashboards.tasks.summary.complianceEvolution.navigation'),
    },
    chart: {
      view: chartViews.evolution,
      containerRef: evolutionPages.ref,
      tooltipLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.complianceEvolution.tooltipLabel' }),
      formatTooltipDetail: ({ completed, total }: { completed: number; total: number }) =>
        intl.formatMessage(
          { id: 'dashboards.tasks.summary.complianceEvolution.tooltipDetail' },
          { completed, total },
        ),
      emptyLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.complianceEvolution.noOrders' }),
      data: evolutionPages.paginate(evolutionPeriods).map(period => ({
        period: period.periodLabel,
        value: period.completionRate === null ? null : Math.round(period.completionRate),
        completed: period.completed,
        total: period.total,
        color: getComplianceColor(period.completionRate ?? 0),
      })),
    },
  };

  const incidents = {
    controls: {
      view: {
        value: chartViews.incidents,
        labels: viewLabels,
        onChange: view => onChartViewChange('incidents', view),
      },
      pager: pagerProps(incidentPages, 'dashboards.tasks.summary.executionIncidents.navigation'),
    },
    totals: incidentCategories.map(category => ({
      key: category.key,
      label: category.label,
      color: category.color,
      total: periods.reduce((total, period) => total + period[category.key], 0),
    })),
    chart: {
      view: chartViews.incidents,
      containerRef: incidentPages.ref,
      colors: incidentCategories.map(category => category.color),
      data: incidentPages.paginate(periods).flatMap(period =>
        incidentCategories.map(category => ({
          period: period.periodLabel,
          category: category.label,
          value: period[category.key],
        })),
      ),
    },
  };

  return { evolution, incidents };
};
